import { UserLocation } from '@/types/location.types';

/**
 * Support contact shown in the profile Help Center.
 *
 * Email is the location's Business Email (synced from the GHL Business
 * Profile), falling back to the Tee Box support inbox when it is not set.
 *
 * Phone is the member's own box: the Box Pro who runs it, then the location's
 * published number, then the Tee Box general line. That resolution mirrors the
 * API's `resolveLocationContact`
 * (api/src/modules/locations/utils/location-contact.util.ts), which backs the
 * check-in "Contact us" link, so a call reaches the same person either way.
 */

/** Email fallback — used only when the location has no Business Email. */
export const GENERAL_SUPPORT_EMAIL = 'support@tbx.golf';
/** Phone fallback — used only when the location carries no number of its own. */
export const GENERAL_SUPPORT_PHONE = '1-801-215-9168';

/**
 * Applied when a Box Pro record has a mobile but no countryCode — matches the
 * API's resolver, so this dials the same number as the check-in link.
 */
const DEFAULT_COUNTRY_CODE = '+1';

/** Digits-only (plus a leading +) so the tel: href is actually dialable. */
export function toTelHref(countryCode: string | undefined, mobile: string): string {
  return `tel:${`${countryCode ?? ''}${mobile}`.replace(/[^\d+]/g, '')}`;
}

/** Pretty-print NANP numbers; anything else is shown as stored. */
export function formatPhone(countryCode: string | undefined, mobile: string): string {
  const raw = `${countryCode ?? ''}${mobile}`.replace(/[^\d+]/g, '');
  const nanp =
    raw.match(/^\+?1(\d{3})(\d{3})(\d{4})$/) ?? raw.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (nanp) return `+1 (${nanp[1]}) ${nanp[2]}-${nanp[3]}`;
  return countryCode ? `${countryCode} ${mobile}` : mobile;
}

export interface SupportContact {
  /** Location Business Email, e.g. `admin@centerville.tbxmail.golf`, else `support@tbx.golf`. */
  email: string;
  emailHref: string;
  /** Display value, e.g. `+1 (801) 215-9168`. */
  phone: string;
  phoneHref: string;
  /** True when the email belongs to the member's location rather than Tee Box. */
  emailIsLocal: boolean;
  /** True when the number belongs to the member's box rather than Tee Box. */
  phoneIsLocal: boolean;
}

export function resolveSupportContact(location?: UserLocation | null): SupportContact {
  const boxPro = location?.boxPro;

  const locationEmail = location?.supportEmail?.trim();
  const email = locationEmail || GENERAL_SUPPORT_EMAIL;
  const mobile = boxPro?.mobile?.trim();
  const defaultPhone = location?.defaultPhoneNumber?.trim();

  let phone: string;
  let phoneHref: string;
  if (mobile) {
    const countryCode = (boxPro?.countryCode || DEFAULT_COUNTRY_CODE).trim();
    phone = formatPhone(countryCode, mobile);
    phoneHref = toTelHref(countryCode, mobile);
  } else if (defaultPhone) {
    // defaultPhoneNumber is stored fully qualified, so it carries no separate
    // country code of its own.
    phone = formatPhone(undefined, defaultPhone);
    phoneHref = toTelHref(undefined, defaultPhone);
  } else {
    // Shown exactly as it has always been shown, rather than reformatted.
    phone = GENERAL_SUPPORT_PHONE;
    phoneHref = toTelHref(undefined, GENERAL_SUPPORT_PHONE);
  }

  return {
    email,
    emailHref: `mailto:${email}`,
    phone,
    phoneHref,
    emailIsLocal: !!locationEmail,
    phoneIsLocal: !!(mobile || defaultPhone),
  };
}
